import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { type ProductSchema } from '@lyka-pet-food/contracts'
import type {
  MealProduct,
  TreatProduct,
  SupplementProduct,
  EnrichedProduct,
  ProductVariant,
  ExtraProduct,
} from '@repo/api-types'
import { useProducts } from '@/composables/useProducts'
import { compareExtrasProducts } from '@/utils/product'

export const useProductStore = defineStore('products', () => {
  const loading = ref(false)

  const meals = ref<MealProduct[]>([])
  const treats = ref<TreatProduct[]>([])
  const supplements = ref<SupplementProduct[]>([])

  /**
   * @deprecated
   * In most cases we should be using meals, treats or supplements directly
   */
  const allProducts = computed<EnrichedProduct[]>(() => [...meals.value, ...supplements.value, ...treats.value])

  const load = async () => {
    loading.value = true
    const { products, error } = await useProducts().getProducts()
    // TODO: Move to error  handling
    if (error) {
      console.error(error)
      loading.value = false
      return
    }
    if (!products) {
      console.error('No products loaded')
      loading.value = false
      return
    }

    meals.value = products.meals
    treats.value = products.treats.sort(compareExtrasProducts)
    supplements.value = products.supplements.sort(compareExtrasProducts)

    loading.value = false
  }

  const getProductById = (id: string) => {
    return allProducts.value?.find((product) => product?.id === id)
  }

  const getProductVariantById = (id: string): ProductVariant | undefined => {
    for (const product of allProducts.value) {
      const variant = product.variants.find((v) => v.id === id)
      if (variant) {
        return variant
      }
    }
  }

  // TODO: Deprecate this
  const getImageUrlByProductId = (id: string) => {
    return getProductById(id)?.images.find((image) => image.imageType === 'icon')?.imageUrl
  }

  // TODO: Deprecate this
  const getImageUrlByProductVariantId = (id: string) => {
    return getProductVariantById(id)?.images.find((image) => image.imageType === 'icon')?.imageUrl
  }

  const getStoryblokImageByProductId = (id: string) => {
    return getProductById(id)?.thumbnail
  }

  const getQuantityTypeInGramByVariantId = (id: string) => {
    return getProductVariantById(id)?.dimension?.weightGrams ?? undefined
  }

  const getProductVariantBySku = (sku: string) => {
    for (const product of allProducts.value) {
      const variant = product.variants.find((productVariant) => productVariant.sku === sku)
      if (variant) {
        return variant
      }
    }
  }

  const getProductVariantByProductIdPouchSize = (id: string, pouchSize: string) => {
    const pouchSizeInNumber = parseInt(pouchSize)
    return allProducts.value
      ?.find((product) => product.id === id)
      ?.variants?.find((variant) => variant.dimension?.weightGrams === pouchSizeInNumber)
  }

  const getMealByVariantId = (id: string): MealProduct | undefined => {
    return meals.value.find((meal) => meal.variants.some((variant) => variant.id === id))
  }

  const getExtraByVariantId = (id: string): ExtraProduct | undefined => {
    const extras: ExtraProduct[] = [...treats.value, ...supplements.value]
    return extras.find((extra) => extra.variants.some((variant) => variant.id === id))
  }

  const getProductByProductVariantId = (id: string): EnrichedProduct | undefined => {
    return allProducts.value?.find((product) => product.variants.find((variant) => variant.id === id))
  }

  const getProductByProductVariantSku = (sku: string): ProductSchema | undefined => {
    return allProducts.value?.find((product) => product.variants.find((variant) => variant.sku === sku))
  }

  return {
    loading,
    meals,
    treats,
    supplements,
    allProducts,
    getStoryblokImageByProductId,
    getImageUrlByProductId,
    getImageUrlByProductVariantId,
    getProductVariantById,
    getProductById,
    getQuantityTypeInGramByVariantId,
    getMealByVariantId,
    getExtraByVariantId,
    getProductByProductVariantId,
    getProductVariantByProductIdPouchSize,
    getProductByProductVariantSku,
    getProductVariantBySku,
    load,
  }
})
